import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, RefreshCw, WifiOff, Clock, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useDownloadFlowStore } from '@/stores/useDownloadFlowStore';
import { toast } from 'sonner';

interface AuthErrorHandlerProps {
  error: AuthError | null;
  onRetry: () => void;
  onCancel: () => void;
  isVisible: boolean;
}

export type AuthError = {
  type: 'user_cancelled' | 'session_timeout' | 'auth_failed' | 'network_error' | 'unknown';
  message: string;
  canRetry: boolean;
  retryCount?: number;
};

const AuthErrorHandler: React.FC<AuthErrorHandlerProps> = ({
  error,
  onRetry,
  onCancel,
  isVisible,
}) => {
  const { isAuthenticated } = useAuth();
  const { endDownloadSession, hideAllModals } = useDownloadFlowStore();
  const [isRetrying, setIsRetrying] = useState(false);

  // Auto-clear error if user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && error) {
      onCancel();
    }
  }, [isAuthenticated, error, onCancel]);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await onRetry();
    } catch (retryError) {
      console.error('Retry failed:', retryError);
      toast.error('Retry failed', {
        description: 'Unable to retry authentication. Please try again later.',
      });
    } finally {
      setIsRetrying(false);
    }
  };

  const handleCancel = () => {
    // Clean up download session and modals
    endDownloadSession();
    hideAllModals();
    
    toast.info('Authentication cancelled', {
      description: 'You can try downloading again at any time.',
    });
    
    onCancel();
  };

  const getErrorIcon = (errorType: AuthError['type']) => {
    switch (errorType) {
      case 'network_error':
        return <WifiOff className="h-6 w-6 text-orange-600" />;
      case 'session_timeout':
        return <Clock className="h-6 w-6 text-yellow-600" />;
      case 'user_cancelled':
        return <X className="h-6 w-6 text-gray-600" />;
      default:
        return <AlertCircle className="h-6 w-6 text-red-600" />;
    }
  };

  const getErrorTitle = (errorType: AuthError['type']) => {
    switch (errorType) {
      case 'user_cancelled':
        return 'Authentication Cancelled';
      case 'session_timeout':
        return 'Session Timeout';
      case 'auth_failed':
        return 'Authentication Failed';
      case 'network_error':
        return 'Network Issue';
      default:
        return 'Authentication Error';
    }
  };

  const getErrorDescription = (error: AuthError) => {
    switch (error.type) {
      case 'user_cancelled':
        return 'Sign-in was cancelled. You can try again or download as a guest if available.';
      case 'session_timeout':
        return 'Your authentication session has expired. Please sign in again to continue.';
      case 'auth_failed':
        return 'Sign-in failed. Please check your credentials and try again.';
      case 'network_error':
        return 'Unable to connect to authentication service. Please check your internet connection.';
      default:
        return error.message || 'An unexpected error occurred during authentication.';
    }
  };

  const getRecoveryOptions = (error: AuthError) => {
    const options = [];

    if (error.canRetry) {
      options.push(
        <Button
          key="retry"
          onClick={handleRetry}
          disabled={isRetrying}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`} />
          {isRetrying ? 'Retrying...' : 'Try Again'}
          {error.retryCount && error.retryCount > 0 && ` (${error.retryCount + 1})`}
        </Button>
      );
    }

    if (error.type === 'network_error') {
      options.push(
        <Button
          key="check-network"
          variant="outline"
          onClick={() => {
            window.open('https://www.google.com', '_blank');
          }}
        >
          Check Network
        </Button>
      );
    }

    options.push(
      <Button
        key="cancel"
        variant="outline"
        onClick={handleCancel}
      >
        Cancel & Return
      </Button>
    );

    return options;
  };

  if (!isVisible || !error) {
    return null;
  }

  return (
    <Card className="w-full max-w-md mx-auto border-l-4 border-l-orange-500">
      <CardHeader>
        <div className="flex items-center gap-3">
          {getErrorIcon(error.type)}
          <div>
            <CardTitle className="text-lg">{getErrorTitle(error.type)}</CardTitle>
            <CardDescription>{getErrorDescription(error)}</CardDescription>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {error.type === 'network_error' && (
          <Alert>
            <WifiOff className="h-4 w-4" />
            <AlertDescription>
              Try refreshing the page or checking your internet connection. 
              If the problem persists, you may be behind a firewall or proxy.
            </AlertDescription>
          </Alert>
        )}

        {error.type === 'session_timeout' && (
          <Alert>
            <Clock className="h-4 w-4" />
            <AlertDescription>
              For security, authentication sessions expire after a period of inactivity. 
              Simply sign in again to continue where you left off.
            </AlertDescription>
          </Alert>
        )}

        {error.retryCount && error.retryCount >= 2 && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Multiple attempts have failed. Consider checking your internet connection 
              or waiting a few minutes before trying again.
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col gap-2">
          {getRecoveryOptions(error)}
        </div>
      </CardContent>
    </Card>
  );
};

export default AuthErrorHandler; 